import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getVacations, createVacation, reviewVacation, deleteVacation } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Palmtree, Plus, Check, X, Clock, Trash2 } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';

export const VacationsPage = () => {
  const { hasRole, user } = useAuth();
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [dateRange, setDateRange] = useState({ from: null, to: null });

  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    vacation_type: 'annual'
  });

  const fetchData = useCallback(async () => {
    try {
      const params = selectedTab !== 'all' ? { status: selectedTab } : {};
      const response = await getVacations(params);
      setVacations(response.data);
    } catch (error) {
      console.error('Failed to fetch vacations:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateRangeSelect = (range) => {
    setDateRange(range);
    if (range?.from) {
      setFormData(prev => ({ ...prev, start_date: format(range.from, 'yyyy-MM-dd') }));
    }
    if (range?.to) {
      setFormData(prev => ({ ...prev, end_date: format(range.to, 'yyyy-MM-dd') }));
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    try {
      await createVacation(formData);
      toast.success('Vacation request submitted');
      setDialogOpen(false);
      setFormData({
        start_date: '',
        end_date: '',
        reason: '',
        vacation_type: 'annual'
      });
      setDateRange({ from: null, to: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleReview = async (id, status) => {
    try {
      await reviewVacation(id, status);
      toast.success(`Request ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review request');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    try {
      await deleteVacation(id);
      toast.success('Request deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete request');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="badge-success">Approved</Badge>;
      case 'rejected':
        return <Badge className="badge-error">Rejected</Badge>;
      default:
        return <Badge className="badge-warning">Pending</Badge>;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      annual: 'Annual Leave',
      sick: 'Sick Leave',
      personal: 'Personal Leave'
    };
    return labels[type] || type;
  };

  const getDuration = (start, end) => {
    try {
      const days = differenceInDays(parseISO(end), parseISO(start)) + 1;
      return `${days} day${days > 1 ? 's' : ''}`;
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="vacations-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="vacations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Manrope']">
            Vacations
          </h1>
          <p className="text-muted-foreground mt-1">
            Request and manage time off
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="request-vacation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Request Time Off
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Manrope']">Request Time Off</DialogTitle>
              <DialogDescription>
                Submit a new vacation or leave request
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateRequest} className="space-y-4">
              <div className="space-y-2">
                <Label>Type of Leave</Label>
                <Select
                  value={formData.vacation_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, vacation_type: value }))}
                >
                  <SelectTrigger data-testid="vacation-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="annual">Annual Leave</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="personal">Personal Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Select Dates</Label>
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={handleDateRangeSelect}
                  className="rounded-md border"
                  numberOfMonths={1}
                />
                {dateRange?.from && dateRange?.to && (
                  <p className="text-sm text-muted-foreground">
                    {getDuration(format(dateRange.from, 'yyyy-MM-dd'), format(dateRange.to, 'yyyy-MM-dd'))}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Reason</Label>
                <Textarea
                  placeholder="Please provide a reason for your request..."
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  data-testid="vacation-reason"
                />
              </div>

              <Button type="submit" className="w-full" data-testid="submit-vacation-btn">
                Submit Request
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Vacations List */}
      <Card>
        <CardContent className="p-0">
          {vacations.length === 0 ? (
            <div className="empty-state py-12">
              <Palmtree className="empty-state-icon" />
              <p className="text-muted-foreground">No vacation requests found</p>
            </div>
          ) : (
            <div className="divide-y">
              {vacations.map((vacation) => (
                <div
                  key={vacation.id}
                  className="p-6 hover:bg-muted/50 transition-colors"
                  data-testid={`vacation-${vacation.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-cyan-50 flex items-center justify-center flex-shrink-0">
                        <Palmtree className="w-6 h-6 text-cyan-600" />
                      </div>
                      <div>
                        {vacation.user_name && hasRole(['admin', 'manager', 'supervisor']) && (
                          <p className="font-semibold">{vacation.user_name}</p>
                        )}
                        <p className="text-sm font-medium">
                          {getTypeLabel(vacation.vacation_type)}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {format(parseISO(vacation.start_date), 'MMM d, yyyy')} - {format(parseISO(vacation.end_date), 'MMM d, yyyy')}
                          <span className="ml-2">({getDuration(vacation.start_date, vacation.end_date)})</span>
                        </p>
                        {vacation.reason && (
                          <p className="text-sm text-muted-foreground mt-2">
                            "{vacation.reason}"
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {getStatusBadge(vacation.status)}
                      
                      {vacation.status === 'pending' && hasRole(['admin', 'manager', 'supervisor']) && vacation.user_id !== user?.id && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 hover:text-emerald-700"
                            onClick={() => handleReview(vacation.id, 'approved')}
                            data-testid={`approve-vacation-${vacation.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-rose-600 hover:text-rose-700"
                            onClick={() => handleReview(vacation.id, 'rejected')}
                            data-testid={`reject-vacation-${vacation.id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {vacation.status === 'pending' && vacation.user_id === user?.id && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => handleDelete(vacation.id)}
                          data-testid={`delete-vacation-${vacation.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
